import React, { useMemo, useState } from "react";
import { Button, Tag, Tooltip } from "antd";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRotateLeft } from "@fortawesome/free-solid-svg-icons";
import { RootState } from "../../../../app/store";
import { BankFloorInterface } from "../../../../interfaces/Bank.interface";
import usePostCommand from "../../../../customHooks/POST_requests/usePostCommand";
import floorButtonLabel from "../../../../utils/floorButtonLabel";
import ActionFloorStrip from "./ActionFloorStrip";
import { SequenceSpec } from "../actionSequenceConfig";

/**
 * The controls for a sequenced action — one command sent once per step, at a
 * floor the operator picks each time.
 *
 * This replaces `ActionControls` for these rows rather than wrapping it. The
 * behaviour components underneath `ActionControls` model a command as one
 * dispatch with an optional paired opposite; a sequence is N dispatches of the
 * SAME command whose only difference is the floor, and it has to know when each
 * one has gone out so it can advance. None of the seven behaviour components
 * report that, and giving all of them a callback for the benefit of one row
 * shape would be a wide change for a narrow need.
 *
 * Picking a floor sends immediately, which is how the VIP panel this replaces
 * has always worked: the operator is walking a car through a route, and a
 * separate confirm press on every leg is a step that surface never had.
 */
export default function ActionSequenceControls({
  sequence,
  command,
  floors,
  colorAction,
  disabled = false,
}: {
  sequence: SequenceSpec;
  /**
   * The Control enum this row puts on the wire. `null` when nothing resolves,
   * which disables the strip — sending at a command of "nothing" is how the
   * pre-configured-VIP builds dropped every step silently at the controller.
   */
  command: number | null;
  /** Eligible floors, already filtered and ordered by the caller. */
  floors: BankFloorInterface[];
  colorAction: string;
  disabled?: boolean;
}) {
  const [t] = useTranslation("global");
  const { theme } = useSelector((state: RootState) => state.home);
  const { triggerPostCommand, isLoadingPostCommand } = usePostCommand();

  /** Floors already chosen, one per completed step, in step order. */
  const [picked, setPicked] = useState<Array<{ floorIndex: number; name: string }>>([]);

  const stepIndex = picked.length;
  const complete = stepIndex >= sequence.steps.length;
  const activeStep = complete ? undefined : sequence.steps[stepIndex];

  // Routed through the shared label rule rather than reading `name` directly:
  // this used to fall back to the floor's INDEX, so a sequence step recorded
  // "10" for the floor the grid calls 11 — the same off-by-one the floor
  // buttons had, in the tag that reports what the operator already picked.
  const floorName = useMemo(
    () => (floorIndex: number) => {
      const floor = floors.find((f) => f.floorIndex === floorIndex);
      return floor ? String(floorButtonLabel(floor)) : String(floorIndex);
    },
    [floors],
  );

  const onSelectFloor = (floorIndex: number) => {
    if (command === null || complete) return;
    triggerPostCommand({
      floor: floorIndex,
      command,
      liftType: "ELEVATOR",
      active: true,
    });
    setPicked((prev) => [...prev, { floorIndex, name: floorName(floorIndex) }]);
  };

  const onReset = () => setPicked([]);

  return (
    <div className="actionCard__sequence" data-testid={`action-sequence-${sequence.id}`}>
      {/* The steps, with what each one resolved to. A step that has been sent
          keeps showing its floor: the operator is building a route and needs to
          see the whole of it, not just the leg they are on. */}
      <div className="generalStyles__flexWrap" style={{ gap: 12, marginBottom: 8 }}>
        {sequence.steps.map((step, index) => {
          const done = index < picked.length;
          const current = index === stepIndex;
          // Done steps carry the row's own colour; the step awaiting a pick is
          // tinted so the operator can see where they are without reading the
          // prompt. Neither state is signalled by colour alone — the value
          // itself changes from "--" to a floor name.
          let tagColor: string | undefined;
          if (done) tagColor = colorAction;
          else if (current) tagColor = "processing";
          return (
            <div key={step.id} data-testid={`action-sequence-step-${step.id}`}>
              <span className="generalStyles__inputLabel">{t(step.labelKey, step.label)}</span>
              <div>
                <Tag color={tagColor}>{done ? picked[index].name : "--"}</Tag>
              </div>
            </div>
          );
        })}
        <div style={{ marginLeft: "auto" }}>
          <Tooltip title={t("monitoringActions.sequence.reset", "Start the sequence over")}>
            <Button
              size="small"
              onClick={onReset}
              disabled={picked.length === 0 || isLoadingPostCommand}
              data-testid={`action-sequence-reset-${sequence.id}`}
              icon={<FontAwesomeIcon icon={faRotateLeft} />}
            >
              {t("general.reset", "Reset")}
            </Button>
          </Tooltip>
        </div>
      </div>

      <p className={`generalStyles__info generalStyles__infoText__${theme}`}>
        {complete
          ? t(sequence.completeKey, sequence.completeText)
          : t(activeStep?.promptKey ?? "", activeStep?.prompt ?? "")}
      </p>

      {/* Reuses the same strip every floor-taking row uses, so a sequence step
          offers exactly the floors the operator is permitted to send to. */}
      <ActionFloorStrip
        floors={floors}
        selectedFloorIndex={null}
        onSelectFloor={onSelectFloor}
        disabled={disabled || complete || command === null || isLoadingPostCommand}
      />
    </div>
  );
}
